// src/utils/tactic/heroLanes.js
// ─────────────────────────────────────────────────────────────────────────
// Which lane does a hero actually get played in?
//
// heroList.json only records a CLASS (Carry / Mage / Fighter / …), which is a
// poor proxy for a lane — every Mage would count as a roamer. The uploaded
// workbook, on the other hand, records the real thing: every scrim row says
// which hero stood in which lane, for both teams. This builds a lane histogram
// per hero out of those rows, and falls back to the class guess only for heroes
// the team has never actually played.
// ─────────────────────────────────────────────────────────────────────────

import { canonicalHeroName, heroBlockKey } from "../heroCatalog";

export const LANES = ["DSL", "JUG", "MID", "ADL", "ROAM"];

/** The workbook calls the roamer SUP; the board calls it ROAM. */
export const LANE_OF_SHEET_ROLE = {
  DSL: "DSL",
  JUG: "JUG",
  MID: "MID",
  ADL: "ADL",
  SUP: "ROAM",
};

const SHEET_ROLES = Object.keys(LANE_OF_SHEET_ROLE);

// Fallback for heroes with no picks on record. Deliberately generous: a hero
// listed under two lanes is better than one confidently filed under the wrong
// one. See LANES_BY_CLASS consumers for how this is surfaced.
export const LANES_BY_CLASS = {
  Carry: ["ADL"],
  Marksman: ["ADL"],
  Mage: ["MID", "ROAM"],
  Assasin: ["JUG", "MID"], // heroList spells it with one "s"
  Assassin: ["JUG", "MID"],
  Fighter: ["DSL", "JUG"],
  Warrior: ["DSL", "JUG"],
  Tank: ["ROAM", "DSL"],
  Support: ["ROAM"],
};

/**
 * Count lane appearances per hero across every parsed game, both teams.
 *
 * @returns {{ byHero: Map, games: number, picks: number }} `byHero` maps a
 *   normalised hero key to { name, counts: {lane: n}, total, top }.
 */
export function buildHeroLaneIndex(games) {
  const byHero = new Map();
  let picks = 0;

  for (const game of games || []) {
    for (const sideKey of ["fsByRole", "opByRole"]) {
      const byRole = game?.[sideKey];
      if (!byRole) continue;

      for (const role of SHEET_ROLES) {
        const raw = byRole[role];
        if (!raw) continue;

        const name = canonicalHeroName(raw);
        const key = heroBlockKey(name);
        if (!key) continue;

        let entry = byHero.get(key);
        if (!entry) {
          entry = { name, counts: {}, total: 0, top: null };
          byHero.set(key, entry);
        }
        const lane = LANE_OF_SHEET_ROLE[role];
        entry.counts[lane] = (entry.counts[lane] || 0) + 1;
        entry.total += 1;
        picks += 1;
      }
    }
  }

  for (const entry of byHero.values()) {
    entry.top = LANES.reduce(
      (best, lane) => ((entry.counts[lane] || 0) > (entry.counts[best] || 0) ? lane : best),
      LANES[0]
    );
  }

  return { byHero, games: (games || []).length, picks };
}

// A lane shows up for a hero if it is their most-played one, or if it carries a
// real share of their picks. Without the share floor a single off-role game
// would file a jungler under support forever.
const SHARE_FLOOR = 0.2;

/** Lanes a hero is listed under, most-played first. Never empty. */
export function lanesForHero(index, heroName, heroClass) {
  const entry = index?.byHero?.get(heroBlockKey(heroName));

  if (entry?.total) {
    const lanes = LANES.filter(
      (lane) => lane === entry.top || (entry.counts[lane] || 0) / entry.total >= SHARE_FLOOR
    );
    return lanes.sort((a, b) => (entry.counts[b] || 0) - (entry.counts[a] || 0));
  }

  return LANES_BY_CLASS[heroClass] || [];
}

/** True when the listing came from real picks rather than the class guess. */
export function hasLaneData(index, heroName) {
  return Boolean(index?.byHero?.get(heroBlockKey(heroName))?.total);
}

/** The hero's most-played lane, or null when there is nothing on record. */
export function primaryLaneFor(index, heroName) {
  return index?.byHero?.get(heroBlockKey(heroName))?.top || null;
}

/** How many times a hero was played in one lane. */
export function pickCount(index, heroName, lane) {
  const entry = index?.byHero?.get(heroBlockKey(heroName));
  if (!entry) return 0;
  return lane ? entry.counts[lane] || 0 : entry.total;
}

/** "JUG ×12 · MID ×2" — the tooltip behind each hero tile. */
export function laneBreakdown(index, heroName) {
  const entry = index?.byHero?.get(heroBlockKey(heroName));
  if (!entry?.total) return "";
  return LANES.filter((lane) => entry.counts[lane])
    .sort((a, b) => entry.counts[b] - entry.counts[a])
    .map((lane) => `${lane} ×${entry.counts[lane]}`)
    .join(" · ");
}
